import React, { useEffect, useState } from 'react';
import { Button, Checkbox, Select, TextBox } from "@veneer/core";
import { Col, Container, Row } from "react-bootstrap";
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '@store/hooks';
import { getFieldError, isValidEmail, isValidPhoneNumber, onSearchDropDown } from 'utils';

// IndexedDB utility functions
const DB_NAME = 'EquipmentContactDB';
const DB_VERSION = 1;
const STORE_NAME = 'equipmentContacts';

const openDB = (): Promise<IDBDatabase> => {
    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);
        
        request.onerror = () => reject(request.error);
        request.onsuccess = () => resolve(request.result);
        
        request.onupgradeneeded = (event) => {
            const db = (event.target as IDBOpenDBRequest).result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
                store.createIndex('userId', 'UserId', { unique: false });
                store.createIndex('rowid', 'rowid', { unique: false });
            }
        };
    });
};

const saveEquipmentContactToDB = async (data: any, rowid: string): Promise<void> => {
    try {
        const db = await openDB();
        const transaction = db.transaction([STORE_NAME], 'readwrite');
        const store = transaction.objectStore(STORE_NAME);
        
        const contactData = {
            ...data,
            id: `${data.UserId?.[0] || 'unknown'}_${rowid}`,
            rowid: rowid,
            timestamp: Date.now()
        };
        
        await store.put(contactData);
        console.log('Equipment contact data saved to IndexedDB:', contactData);
    } catch (error) {
        console.error('Error saving to IndexedDB:', error);
    }
};

const getEquipmentContactFromDB = async (userId: string, rowid: string): Promise<any> => {
    try {
        const db = await openDB();
        const transaction = db.transaction([STORE_NAME], 'readonly');
        const store = transaction.objectStore(STORE_NAME);
        
        const id = `${userId}_${rowid}`;
        const request = store.get(id);
        
        return new Promise((resolve, reject) => {
            request.onerror = () => reject(request.error);
            request.onsuccess = () => {
                if (request.result) {
                    console.log('Found existing equipment contact data in IndexedDB:', request.result);
                    resolve(request.result);
                } else {
                    resolve(null);
                }
            };
        });
    } catch (error) {
        console.error('Error retrieving from IndexedDB:', error);
        return null;
    }
};

interface EquipmentContactProps {
    equipmentContact: any;
    countryList: any[];
    countryCodes: any[];
    rowid: any;
    onSave: (data: any) => void;
}

const EquipmentContact: React.FC<EquipmentContactProps> = ({ equipmentContact, countryList, countryCodes, rowid, onSave }) => {   
    const { t } = useTranslation();
    const [filteredCountryCodes, setFilteredCountryCodes] = useState(countryCodes || []);
    const [filteredCountryList, setFilteredCountryList] = useState(countryList || []);
    const [filteredAltCountryCodes, setFilteredAltCountryCodes] = useState(countryCodes || []);
    const defaultRequiredFields = ['FirstName', 'LastName', 'countryId', 'Email', 'PhoneNumber'];
    const [errors, setErrors] = useState<Record<string, string>>({
        FirstName: '',
        LastName: '',
        PhoneNumber: '',
        Extension: '',
        AlternatePhoneNumber: '',
        AlternateExtension: '',
        Email: '',
        countryId: ''
    });
    const [form, setForm] = useState({ 
        UserId: [''] as string[],
        AccountId: '',
        fullname: '',
        FirstName: '', 
        LastName: '', 
        PhoneNumber: '',
        Extension: '',
        AlternatePhoneNumber: '',
        AlternateExtension: '',
        Email: '',
        countryName: '',
        cpf: '',
        neighbourhood: '',
        stateInscription: '',
        countryId: [''] as string[],
        accountType: null,
        countryshortcode: '',
        countrycode: '',
        LangShortCode: null,
        phNumAlternateCountryCode: [''] as string[],
        phNumCountryCode: [''] as string[]
    });


    useEffect(() => {
        console.log('EquipmentContact component mounted with props:', { equipmentContact, countryList, countryCodes, rowid });
        const loadEquipmentContactData = async () => {
            if (equipmentContact) {
                console.log('Populating form with equipment contact data:', equipmentContact);
                // Check if there's existing modified data in IndexedDB first
                const userId = equipmentContact.UserId?.[0] || equipmentContact.UserId;
                if (userId && rowid) {
                    const existingData = await getEquipmentContactFromDB(userId, rowid);
                    console.log('Existing data retrieved from IndexedDB:', existingData);
                    if (existingData) {
                        // Use data from IndexedDB if available (previously modified)
                        console.log('Loading modified data from IndexedDB:', existingData);
                        setForm({
                            UserId: Array.isArray(existingData.UserId) ? existingData.UserId : [''],
                            AccountId: existingData.AccountId || '',
                            fullname: existingData.fullname || '',
                            FirstName: existingData.FirstName || '',
                            LastName: existingData.LastName || '',
                            PhoneNumber: existingData.PhoneNumber || '',
                            Extension: existingData.Extension || '',
                            AlternatePhoneNumber: existingData.AlternatePhoneNumber || '',
                            AlternateExtension: existingData.AlternateExtension || '',
                            Email: existingData.Email || '',
                            countryName: existingData.countryName || '',
                            cpf: existingData.cpf || '',
                            neighbourhood: existingData.neighbourhood || '',
                            stateInscription: existingData.stateInscription || '',
                            countryId: Array.isArray(existingData.countryId) ? existingData.countryId : [''],
                            accountType: existingData.accountType || null,
                            countryshortcode: existingData.countryshortcode || '',
                            countrycode: existingData.countrycode || '',
                            LangShortCode: existingData.LangShortCode || null,
                            phNumAlternateCountryCode: Array.isArray(existingData.phNumAlternateCountryCode) ? existingData.phNumAlternateCountryCode : [''],
                            phNumCountryCode: Array.isArray(existingData.phNumCountryCode) ? existingData.phNumCountryCode : ['']
                        });
                        return;
                    }
                }
                
                // Use original equipmentContact data if no modified data found
                setForm({
                    UserId: Array.isArray(equipmentContact.UserId) ? equipmentContact.UserId : [''],
                    AccountId: equipmentContact.AccountId || '',
                    fullname: equipmentContact.fullname || '',
                    FirstName: equipmentContact.FirstName || '',
                    LastName: equipmentContact.LastName || '',
                    PhoneNumber: equipmentContact.PhoneNumber || '',
                    Extension: equipmentContact.Extension || '',
                    AlternatePhoneNumber: equipmentContact.AlternatePhoneNumber || '',
                    AlternateExtension: equipmentContact.AlternateExtension || '',
                    Email: equipmentContact.Email || '',
                    countryName: equipmentContact.countryName || '',
                    cpf: equipmentContact.cpf || '',
                    neighbourhood: equipmentContact.neighbourhood || '',
                    stateInscription: equipmentContact.stateInscription || '',
                    countryId: Array.isArray(equipmentContact.countryId) ? equipmentContact.countryId : [''],
                    accountType: equipmentContact.accountType || null,
                    countryshortcode: equipmentContact.countryshortcode || '',
                    countrycode: equipmentContact.countrycode || '',
                    LangShortCode: equipmentContact.LangShortCode || null,
                    phNumAlternateCountryCode: Array.isArray(equipmentContact.phNumAlternateCountryCode) ? equipmentContact.phNumAlternateCountryCode : [''],
                    phNumCountryCode: Array.isArray(equipmentContact.phNumCountryCode) ? equipmentContact.phNumCountryCode : ['']
                });
            }
        };
        loadEquipmentContactData();
    }, [equipmentContact, rowid])

    useEffect(() => {
        if (countryCodes != undefined && countryList != undefined) {
            setFilteredCountryCodes(countryCodes);
            setFilteredAltCountryCodes(countryCodes);
            setFilteredCountryList(countryList);
        }
    }, [countryList, countryCodes])
    
    const validateField = (name: string, value: string | string[]) => {
        let error = "";
        
        // Convert array values to string for validation
        const stringValue = Array.isArray(value) ? (value[0] || '') : value;
        
        // Check if field is required
        if (defaultRequiredFields.includes(name) && (!stringValue || stringValue.trim() === '')) {
            error = t('HPI.ErrorMessage.RequiredFields', { defaultValue: 'This field is required.' });
        }
        
        // Email format validation
        if (name === "Email" && stringValue && !isValidEmail(stringValue)) {
            error = t('HPI.ErrorMessage.InvalidEmailFormat', { defaultValue: 'Invalid email format' });
        }
        
        // Phone number validation
        if ((name === "PhoneNumber" || name === "AlternatePhoneNumber") && stringValue && !isValidPhoneNumber(stringValue)) {
            error = t('HPI.ErrorMessage.InvalidNumber', { defaultValue: 'Phone number must be 8-15 digits long and contain only numbers.' });
        }
        
        // Name validation (no special characters except spaces, hyphens, and apostrophes)
        if ((name === "FirstName" || name === "LastName") && stringValue) {
            const nameRegex = /^[a-zA-Z\s\-\']+$/;
            if (!nameRegex.test(stringValue)) {
                error = t('HPI.ErrorMessage.InvalidName', { defaultValue: 'Name can only contain letters, spaces, hyphens, and apostrophes.' });
            }
        }
        
        setErrors((prevState) => ({
            ...prevState,
            [name]: error,
        }));
        
        return !error;
    };
    const onSelectChangeHandler = (selectedOption: any, k: any) => {
        // Handle array format for Select components
        const value = selectedOption && selectedOption.value ? [selectedOption.value] : [''];
        
        // Clear error when field is modified
        setErrors(prevErrors => ({
            ...prevErrors,
            [k]: ''
        }));
        
        setForm((prevData) => ({ ...prevData, [k]: value }));
        console.log(selectedOption, k);
        
        // Sync primary country code and Country/Region fields
        if (k === 'phNumCountryCode') {
            // When primary country code changes, update Country/Region too
            const selectedCountryCode = selectedOption?.value;
            const matchingCountry = countryList.find(country => 
                country.value === selectedCountryCode || 
                country.label?.includes(selectedCountryCode)
            );
            
            setForm((prevData) => ({ 
                ...prevData, 
                countryId: matchingCountry ? [matchingCountry.value] : value,
                countryName: matchingCountry?.label || selectedOption?.label || '',
                countrycode: selectedOption?.value || '',
                countryshortcode: selectedOption?.shortcode || ''
            }));
            
            // Clear countryId error as well since they're linked
            setErrors(prevErrors => ({
                ...prevErrors,
                countryId: ''
            }));
            
        } else if (k === 'countryId') {
            // When Country/Region changes, update primary country code too
            const selectedCountryId = selectedOption?.value;
            const matchingCountryCode = countryCodes.find(code => 
                code.value === selectedCountryId || 
                selectedOption?.label?.includes(code.label)
            );
            
            setForm((prevData) => ({ 
                ...prevData, 
                phNumCountryCode: matchingCountryCode ? [matchingCountryCode.value] : [''],
                countryName: selectedOption?.label || '',
                countrycode: matchingCountryCode?.value || '',
                countryshortcode: matchingCountryCode?.shortcode || ''
            }));
            
            // Clear phNumCountryCode error as well since they're linked
            setErrors(prevErrors => ({
                ...prevErrors,
                phNumCountryCode: ''
            }));
        }
        
        // Validate the field
        validateField(k, value);
    };
    const handleChange = (v: string, e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        
        // Clear error when field is modified
        setErrors(prevErrors => ({
            ...prevErrors,
            [name]: ''
        }));
        
        setForm({
            ...form,
            [name]: value,
        });
        
        // Validate the field after change
        validateField(name, value);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Validate all required fields before submission
        let isValid = true;
        const newErrors: Record<string, string> = {};
        
        defaultRequiredFields.forEach(field => {
            const value = form[field as keyof typeof form];
            // Handle null values by converting to empty string
            const safeValue = value === null ? '' : value;
            
            if (!validateField(field, safeValue)) {
                isValid = false;
            }
        });
        
        // Additional validation for specific fields
        if (form.Email && !isValidEmail(form.Email)) {
            setErrors(prev => ({ ...prev, Email: t('HPI.ErrorMessage.InvalidEmailFormat', { defaultValue: 'Invalid email format' }) }));
            isValid = false;
        }
        
        if (form.PhoneNumber && !isValidPhoneNumber(form.PhoneNumber)) {
            setErrors(prev => ({ ...prev, PhoneNumber: t('HPI.ErrorMessage.InvalidNumber', { defaultValue: 'Phone number must be 8-15 digits long and contain only numbers.' }) }));
            isValid = false;
        }
        
        if (!isValid) {
            console.log('Form validation failed', errors);
            return;
        }
        
        // Save form data to IndexedDB before calling onSave
        if (rowid) {
            await saveEquipmentContactToDB(form, rowid);
        }
        
        // Handle form submission logic here
        onSave(form);
    };

    return (
        <form
            id="equipment-contact-form"
            className="equipment-contact-form"
            onSubmit={handleSubmit}
        >

            <Row>
                <Col col={12} md={6}>
                    <TextBox
                        label={t(
                            "HPI.CreateCase.address.Equipmentcontactfname",
                            { defaultValue: "Equipment Contact First Name" }
                        )}
                        placeholder={t(
                            "HPI.CreateCase.Placeholder.EnterEquipmentContactFirstName",
                            { defaultValue: "Enter equipment contact first name" }
                        )}
                        separateLabel
                        required
                        className="textbox"
                        name="FirstName"
                        value={form.FirstName}
                        onChange={(v: any, e) => handleChange(v, e)}
                        onBlur={(e) => validateField('FirstName', e.target.value)}
                        error={!!errors.FirstName}
                        helperText={errors.FirstName}
                    />
                </Col>
                <Col col={12} md={6}>
                    <TextBox
                        label={t(
                            "HPI.CreateCase.address.Equipmentcontactlname",
                            { defaultValue: "Equipment Contact Last Name" }
                        )}
                        placeholder={t(
                            "HPI.CreateCase.Placeholder.EnterEquipmentContactLastName",
                            { defaultValue: "Enter equipment contact last name" }
                        )}
                        separateLabel
                        required
                        className="textbox"
                        name="LastName"
                        value={form.LastName}
                        onChange={(v: any, e) => handleChange(v, e)}
                        onBlur={(e) => validateField('LastName', e.target.value)}
                        error={!!errors.LastName}
                        helperText={errors.LastName}
                    />
                </Col>

                <Col md={6} lg={2}>
                    <Select
                        separateLabel
                        options={filteredCountryCodes || []}
                        showSearch
                        required
                        name="phNumCountryCode"
                        label={t("HPI.ProfilePage.cs.SelectCountryCode", {
                            defaultValue: "Code",
                        })}
                        placement="bottom"
                        placeholder={t("HPI.ProfilePage.cs.SelectCountryCode", {
                            defaultValue: "Code",
                        })}
                        className="textbox country-code"
                        value={form.phNumCountryCode}
                        onChange={(selectedOption: any) =>
                            onSelectChangeHandler(selectedOption, "phNumCountryCode")
                        }
                        onSearch={(value: any) => {
                            let filtered: any = onSearchDropDown(countryCodes, value);
                            setFilteredCountryCodes(filtered);
                        }}
                        clearIcon={false}
                        error={!!errors.phNumCountryCode}
                        helperText={errors.phNumCountryCode}

                    />
                </Col>
                <Col lg={4} md={6} >
                    <label className="custom-label">{t("HPI.CreateCase.Contactinformation.Phonenumber", { defaultValue: 'Phone Number' })}
                    </label>
                    <TextBox
                        placeholder={t('HPI.CreateCase.Placeholder.EnterPhoneNumber', { defaultValue: 'Enter phone number' })}
                        separateLabel
                        required
                        name="PhoneNumber"
                        className="textbox"
                        value={form.PhoneNumber}
                        onChange={(v: any, e) => handleChange(v, e)}
                        onBlur={(e) => validateField('PhoneNumber', e.target.value)}
                        error={!!errors.PhoneNumber}
                        helperText={errors.PhoneNumber}
                    />
                </Col>
                <Col md={6}>
                    <TextBox
                        label={t("HPI.CreateCase.Contactinformation.Extension", { defaultValue: 'Extension' })}
                        name="Extension"
                        placeholder={t('HPI.CreateCase.Placeholder.EnterExtension', { defaultValue: 'Enter extension' })}
                        separateLabel
                        className="textbox"
                        value={form.Extension}
                        onChange={(v: any, e) => handleChange(v, e)}
                        error={!!errors.Extension}
                        helperText={errors.Extension}

                    />
                </Col>
                <Col md={6} lg={2}>
                    <Select
                        separateLabel
                        options={filteredAltCountryCodes || []}
                        showSearch
                        name="phNumAlternateCountryCode"
                        label={t('HPI.ProfilePage.cs.SelectCountryCode', { defaultValue: 'Code' })}
                        placement="bottom"
                        placeholder={t('HPI.ProfilePage.cs.SelectCountryCode', { defaultValue: 'Code' })}
                        className="textbox country-code"
                        value={form.phNumAlternateCountryCode}
                        clearIcon={false}
                        onChange={(selectedOption: any) =>
                            onSelectChangeHandler(selectedOption, "phNumAlternateCountryCode")
                        }
                        onSearch={(value: any) => {
                            let filtered: any = onSearchDropDown(countryCodes, value);
                            setFilteredAltCountryCodes(filtered);
                        }}
                    />
                </Col>
                <Col lg={4} md={6} >
                    <label className="custom-label">{t("HPI.CreateCase.Contactinformation.AlternatePhoneNumber", { defaultValue: 'Alternate Phone Number' })}
                    </label>
                    <TextBox
                        placeholder={t('HPI.CreateCase.Placeholder.EnterPhoneNumber', {
                            defaultValue: 'Alternate or mobile phone number'
                        })}
                        separateLabel
                        name="AlternatePhoneNumber"
                        className="textbox"
                        value={form.AlternatePhoneNumber}
                        onChange={(v: any, e) => handleChange(v, e)}
                        onBlur={(e) => validateField('AlternatePhoneNumber', e.target.value)}
                        error={!!errors.AlternatePhoneNumber}
                        helperText={errors.AlternatePhoneNumber}
                    />
                </Col>
                <Col md={6}>
                    <TextBox
                        label={t("HPI.CreateCase.Contactinformation.AlternateExtension", { defaultValue: 'Alternate Extension' })}
                        name="AlternateExtension"
                        placeholder={t('HPI.CreateCase.Placeholder.EnterExtension', { defaultValue: 'Enter alternate extension' })}
                        separateLabel
                        className="textbox"
                        value={form.AlternateExtension}
                        onChange={(v: any, e) => handleChange(v, e)}
                        error={!!errors.AlternateExtension}
                        helperText={errors.AlternateExtension}

                    />
                </Col>
                <Col md={6}>
                    <Select
                        required
                        showSearch
                        placement="bottom"
                        options={filteredCountryList || []}
                        label={t("HPI.CreateCase.address.CountryRegion", { defaultValue: 'Country/Region' })}
                        helperTextVisibility="auto"
                        onSearch={(value: any) => {
                            let filtered: any = onSearchDropDown(countryList, value);
                            setFilteredCountryList(filtered);
                        }}
                        separateLabel
                        name="countryId"
                        className="textbox"
                        value={form?.countryId}
                        onChange={(selectedOption: any) =>
                            onSelectChangeHandler(selectedOption, "countryId")
                        }
                        error={!!errors.countryId}
                        helperText={errors.countryId}
                    />
                </Col>

                <Col md={6}>
                    <TextBox
                        separateLabel
                        label={t("HPI.CreateCase.Contactinformation.Email", { defaultValue: 'Email' })}
                        type="email"
                        name="Email"
                        required
                        placeholder={t('HPI.CreateCase.Placeholder.EnterEmail', { defaultValue: 'Enter email' })}
                        className="textbox"
                        value={form.Email}
                        onChange={(v: any, e) => handleChange(v, e)}
                        onBlur={(e) => validateField('Email', e.target.value)}
                        error={!!errors.Email}
                        helperText={errors.Email}
                    />
                </Col>


            </Row>

            <Row className="mt-3 mb-4">

                <Col col={12} md={2}>
                    <Button type="submit">
                        {t("HPI.btnAddEquipementContact", {
                            defaultValue: "Add Equipment Contact",
                        })}
                    </Button>
                </Col>
            </Row>
        </form >
    );
};

export default EquipmentContact;